import type {
  GridDataItem,
  GridDataItemRaw,
  ComputedGridConfig,
} from "@/types/grid";
import type {
  ColumnMappingSuggestion,
  UploadedDataColumn,
  UploadedData,
  MergeConflict,
} from "@/types/bulk-import";

/**
 * Store for a single bulk import process: edit upload, mapping and merge conflicts.
 * e.g Production lines import state.
 */
export const useBulkImportStore = defineStore("bulk-import", () => {
  const currentPageSetupId: Ref<string | undefined> = ref();
  const gridConfig: Ref<ComputedGridConfig | undefined> = ref();
  const dataItems: Ref<GridDataItem<GridDataItemRaw>[]> = ref([]);
  const deletedItems: Ref<GridDataItem<GridDataItemRaw>[]> = ref([]);
  const mappingSuggestions: Ref<ColumnMappingSuggestion[]> = ref([]);
  const uploadedData: Ref<UploadedData> = ref([]);
  const uploadedDataColumns: Ref<UploadedDataColumn[]> = ref([]);
  const mergeConflicts = ref<MergeConflict[]>([]);
  const unsavedChangesState = reactive({
    hasUnsavedChanges: false,
    showLeaveConfirmation: false,
    confirmationLeaveRoute: undefined as string | undefined,
  });

  const hasCurrentPageSetup = computed(
    () => currentPageSetupId.value !== undefined,
  );

  const hasMergeConflicts = computed(() => mergeConflicts.value.length > 0);

  const isMappingPending = computed(() => {
    const requiredImportableColumns = [
      ...(gridConfig.value?.importableColumnsHash.values() ?? []),
    ].filter((col) => !col.hidden && col.required);

    if (
      !requiredImportableColumns.length ||
      !uploadedData.value.length ||
      !uploadedDataColumns.value.length
    ) {
      return false;
    }

    const mappedFields = mappingSuggestions.value
      .filter(({ mappedColumnIndex }) => mappedColumnIndex !== undefined)
      .map(({ configColumn }) => configColumn.field);

    const unmappedRequired = requiredImportableColumns.filter(
      (col) => !mappedFields.includes(col.field),
    );

    return unmappedRequired.length > 0;
  });

  /**
   * Initializes the bulk import store. This must be called before navigating to bulk-import page and must be set during the whole upload/mapping process.
   */
  const init = (pageSetupId: string) => {
    // Prevent initializing for the same already initialized import page
    if (currentPageSetupId.value && currentPageSetupId.value === pageSetupId) {
      return;
    }
    reset();
    currentPageSetupId.value = pageSetupId;
  };

  /**
   * Reset the bulk import store, resetting all state.
   * This should be called when navigating away from the bulk import page as long as is not upload or mapping pages.
   * Right now is called in the middleware for import.
   */
  const reset = () => {
    currentPageSetupId.value = undefined;
    gridConfig.value = undefined;
    dataItems.value = [];
    deletedItems.value = [];
    mergeConflicts.value = [];
    mappingSuggestions.value = [];
    uploadedData.value = [];
    uploadedDataColumns.value = [];
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  };

  const refreshUnsavedChanges = () => {
    unsavedChangesState.hasUnsavedChanges = gridHasUnsavedChanges({
      dataItems: dataItems.value,
      deletedItems: deletedItems.value,
    });
  };

  function confirmUnsavedChangesBeforeLeaving(toRoute: string) {
    refreshUnsavedChanges();

    if (!unsavedChangesState.hasUnsavedChanges) {
      unsavedChangesState.showLeaveConfirmation = false;
      unsavedChangesState.confirmationLeaveRoute = undefined;
      return false;
    }

    unsavedChangesState.showLeaveConfirmation = true;
    unsavedChangesState.confirmationLeaveRoute = toRoute;
    return true;
  }

  return {
    currentPageSetupId,
    hasCurrentPageSetup,
    gridConfig,
    dataItems,
    deletedItems,
    isMappingPending,
    mappingSuggestions,
    uploadedData,
    uploadedDataColumns,
    hasMergeConflicts,
    mergeConflicts,
    unsavedChangesState,
    init,
    reset,
    refreshUnsavedChanges,
    confirmUnsavedChangesBeforeLeaving,
  };
});
